import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  hämtaKanalinkopare, skapaKanalinkopare, hämtaKanalaktiviteter, hämtaSäljare,
  Kanalinkopare, Kanalaktivitet, Saljare,
} from '../api/client';
import { TEMA } from '../theme';

const tomtUtkast = { namn: '', ansvarsområde: '', telefon: '', email: '' };

const sektionsLabel: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  fontWeight: 500,
  color: TEMA.accent,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  margin: '0 0 12px',
};

const fältLabel: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  color: TEMA.inkFaint,
  margin: '0 0 4px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  fontSize: '14px',
  fontFamily: "'Inter', sans-serif",
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bg,
  color: TEMA.ink,
  boxSizing: 'border-box',
  outline: 'none',
};

const btnPrimärStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.accent}`,
  background: TEMA.accent,
  color: TEMA.onAccent,
  cursor: 'pointer',
};

const btnGhostStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: 'transparent',
  color: TEMA.inkSoft,
  cursor: 'pointer',
};

const listKortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '16px 20px',
  marginBottom: '10px',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease',
};

export default function KanalInkopareLista() {
  const navigate = useNavigate();
  const [inköpare, setInköpare] = useState<Kanalinkopare[]>([]);
  const [aktiviteter, setAktiviteter] = useState<Kanalaktivitet[]>([]);
  const [säljare, setSäljare] = useState<Saljare[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);

  const [visaNy, setVisaNy] = useState(false);
  const [utkast, setUtkast] = useState(tomtUtkast);
  const [sparar, setSparar] = useState(false);
  const [sparaFel, setSparaFel] = useState<string | null>(null);

  useEffect(() => {
    hämtaData();
  }, []);

  function hämtaData() {
    setLaddar(true);
    setFel(null);
    Promise.all([hämtaKanalinkopare(), hämtaKanalaktiviteter(), hämtaSäljare()])
      .then(([ik, akt, s]) => {
        setInköpare(ik.data);
        setAktiviteter(akt.data);
        setSäljare(s.data);
        setLaddar(false);
      })
      .catch(err => {
        console.error('Kunde inte ladda aktiviteter:', err);
        setFel('Kunde inte ladda sidan just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }

  function öppnaNy() {
    setUtkast(tomtUtkast);
    setSparaFel(null);
    setVisaNy(true);
  }

  async function sparaNy() {
    if (!utkast.namn.trim()) {
      setSparaFel('Namn krävs.');
      return;
    }
    setSparar(true);
    setSparaFel(null);
    try {
      await skapaKanalinkopare(utkast);
      setVisaNy(false);
      hämtaData();
    } catch (err) {
      console.error('Kunde inte skapa kanalinköpare:', err);
      setSparaFel('Kunde inte spara. Försök igen.');
    } finally {
      setSparar(false);
    }
  }

  if (laddar) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>;
  if (fel) return <p style={{ color: '#D9695F', fontFamily: "'Inter', sans-serif" }}>{fel}</p>;

  const inkopareNamn = (id: string) => inköpare.find(i => i.id === id)?.namn || 'Okänd inköpare';
  const säljareNamn = (id?: string | null) => säljare.find(s => s.id === id)?.namn || '—';

  const sorteradeAktiviteter = [...aktiviteter].sort(
    (a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime()
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, margin: 0 }}>Aktiviteter</h1>
        {!visaNy && (
          <button onClick={öppnaNy} style={btnPrimärStyle}>
            + Lägg till kanalinköpare
          </button>
        )}
      </div>

      {visaNy && (
        <div style={{
          background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '8px',
          padding: '22px 24px', marginBottom: '28px',
        }}>
          {sparaFel && <p style={{ color: '#D9695F', fontSize: '13px', margin: '0 0 12px', fontFamily: "'Inter', sans-serif" }}>{sparaFel}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
            <div>
              <p style={fältLabel}>Namn</p>
              <input value={utkast.namn} onChange={e => setUtkast(u => ({ ...u, namn: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <p style={fältLabel}>Ansvarsområde</p>
              <input placeholder="T.ex. Rött vin, Sydeuropa" value={utkast.ansvarsområde} onChange={e => setUtkast(u => ({ ...u, ansvarsområde: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <p style={fältLabel}>Telefon</p>
              <input value={utkast.telefon} onChange={e => setUtkast(u => ({ ...u, telefon: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <p style={fältLabel}>E-post</p>
              <input value={utkast.email} onChange={e => setUtkast(u => ({ ...u, email: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={sparaNy} disabled={sparar} style={{ ...btnPrimärStyle, opacity: sparar ? 0.6 : 1 }}>
              {sparar ? 'Sparar...' : 'Spara'}
            </button>
            <button onClick={() => setVisaNy(false)} disabled={sparar} style={btnGhostStyle}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      <p style={sektionsLabel}>Inköpare</p>

      {inköpare.length === 0 && !visaNy && (
        <p style={{ color: TEMA.inkFaint, textAlign: 'center', marginTop: '32px', fontFamily: "'Inter', sans-serif" }}>
          Inga kanalinköpare registrerade än.
        </p>
      )}

      {inköpare.map(i => (
        <div
          key={i.id}
          onClick={() => navigate(`/anbud/aktiviteter/${i.id}`)}
          style={listKortStyle}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = TEMA.inkSoft; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = TEMA.border; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <span style={{ fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink }}>{i.namn}</span>
            {i.ansvarsområde && (
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '11px', letterSpacing: '0.04em', padding: '3px 10px',
                borderRadius: '2px', border: `1px solid ${TEMA.border}`, color: TEMA.inkSoft,
              }}>
                {i.ansvarsområde}
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
            {[i.telefon, i.email].filter(Boolean).join(' · ') || 'Ingen kontaktinfo registrerad'}
          </p>
        </div>
      ))}

      {inköpare.length > 0 && (
        <>
          <p style={{ ...sektionsLabel, marginTop: '32px' }}>Senaste aktiviteter</p>

          {sorteradeAktiviteter.length === 0 ? (
            <p style={{ color: TEMA.inkFaint, textAlign: 'center', marginTop: '32px', fontFamily: "'Inter', sans-serif" }}>
              Inga aktiviteter registrerade än.
            </p>
          ) : (
            sorteradeAktiviteter.map(akt => {
              const ärMöte = akt.typ === 'möte';
              const typFärg = ärMöte ? TEMA.positive : TEMA.accent;
              return (
                <div
                  key={akt.id}
                  onClick={() => navigate(`/anbud/aktiviteter/${akt.kanalinkopare_id}`)}
                  style={listKortStyle}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = TEMA.inkSoft; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = TEMA.border; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: '11px', letterSpacing: '0.04em', padding: '3px 10px',
                      borderRadius: '2px', border: `1px solid ${typFärg}`, color: typFärg,
                    }}>
                      {ärMöte ? 'FYSISKT MÖTE' : 'PROVINLÄMNING'}
                    </span>
                    <span style={{ fontFamily: "'Fraunces', serif", fontSize: '15px', color: TEMA.ink }}>{inkopareNamn(akt.kanalinkopare_id)}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'IBM Plex Mono', monospace" }}>{akt.datum}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                    {säljareNamn(akt.saljare_id)}
                    {akt.notat ? ` · ${akt.notat}` : ''}
                  </p>
                </div>
              );
            })
          )}
        </>
      )}
    </div>
  );
}